import { ChevronsLeft, ChevronsRight, Menu as MenuIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useState, type ComponentType, type ReactNode } from 'react';
import { Count } from './Badge';
import { Tooltip, TooltipProvider } from './Overlays';

const KEY = 'bf-sidebar-collapsed';

interface ShellState {
  collapsed: boolean;
  toggleCollapsed: () => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
}
const ShellContext = createContext<ShellState | null>(null);
export function useShell(): ShellState {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useShell necesita AppShell');
  return ctx;
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

/** Estructura general: nav lateral colapsable a la izquierda y contenido con barra superior. */
export function AppShell({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      try {
        window.localStorage.setItem(KEY, c ? '0' : '1');
      } catch {
        /* sin almacenamiento: no se recuerda, no pasa nada */
      }
      return !c;
    });
  }, []);

  return (
    <ShellContext.Provider value={{ collapsed, toggleCollapsed, mobileOpen, setMobileOpen }}>
      <TooltipProvider>
        <div className="bf-app" data-collapsed={collapsed} data-mobile-open={mobileOpen}>
          {sidebar}
          {mobileOpen && <div className="bf-mobile-scrim" onClick={() => setMobileOpen(false)} aria-hidden />}
          <div className="bf-main">{children}</div>
        </div>
      </TooltipProvider>
    </ShellContext.Provider>
  );
}

export interface NavItemDef {
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
  count?: number;
  soon?: boolean;
  active?: boolean;
}

export interface NavGroupDef {
  key: string;
  title?: string;
  items: NavItemDef[];
}

type LinkLike = ComponentType<{ to: string; className?: string; children?: ReactNode; 'aria-current'?: 'page'; onClick?: () => void }>;

export function Sidebar({ brand, groups, footer, Link }: { brand: ReactNode; groups: NavGroupDef[]; footer?: ReactNode; Link: LinkLike }) {
  const { collapsed, toggleCollapsed, setMobileOpen } = useShell();
  return (
    <aside className="bf-sidebar" aria-label="Navegación principal">
      <div className="bf-sidebar-head">
        <div className="bf-sidebar-brand">{brand}</div>
        <button
          type="button"
          className="bf-icon-btn bf-sidebar-collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
        >
          {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
        </button>
      </div>
      <nav className="bf-sidebar-nav">
        {groups.map((g) => (
          <div className="bf-nav-group" key={g.key}>
            {g.title && <div className="bf-nav-group-title">{g.title}</div>}
            {g.items.map((item) => {
              const content = (
                <>
                  {item.icon}
                  <span className="bf-nav-label">{item.label}</span>
                  {item.soon ? <span className="bf-nav-soon">Pronto</span> : <Count value={item.count ?? 0} />}
                </>
              );
              const tooltip = item.soon ? `${item.label} (pronto)` : item.label;
              return (
                <Tooltip key={item.key} content={tooltip} disabled={!collapsed}>
                  {item.soon ? (
                    <span className="bf-nav-item" aria-disabled="true" tabIndex={0}>
                      {content}
                    </span>
                  ) : (
                    <Link to={item.to} className="bf-nav-item" aria-current={item.active ? 'page' : undefined} onClick={() => setMobileOpen(false)}>
                      {content}
                    </Link>
                  )}
                </Tooltip>
              );
            })}
          </div>
        ))}
      </nav>
      {footer && <div className="bf-sidebar-foot">{footer}</div>}
    </aside>
  );
}

export function Topbar({ title, children }: { title: ReactNode; children?: ReactNode }) {
  const { setMobileOpen } = useShell();
  return (
    <header className="bf-topbar">
      <button type="button" className="bf-icon-btn bf-mobile-only" onClick={() => setMobileOpen(true)} aria-label="Abrir menú">
        <MenuIcon />
      </button>
      <h1 className="bf-topbar-title">{title}</h1>
      <div className="bf-topbar-spacer" />
      <div className="bf-topbar-actions">{children}</div>
    </header>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="bf-page-head">
      <div>
        <h2 className="bf-page-title">{title}</h2>
        {subtitle && <p className="bf-page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="bf-page-actions">{actions}</div>}
    </div>
  );
}
